import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, CLAVE_AGENTE } from '../config/api';

/** Un agente del usuario, con su número de WhatsApp. */
export interface AgenteResumen {
  id: number;
  nombre: string;
  activo: boolean;
  es_principal: boolean;
  numero: string | null;
  modo: 'api' | 'qr';
  conectado: boolean;
  incluido_en_plan: boolean;
}

interface Ctx {
  agentes: AgenteResumen[];
  actual: AgenteResumen | null;
  limite: number;
  usados: number;
  enPrueba: boolean;
  cargando: boolean;
  /** Cambia el agente con el que se trabaja en todo el panel (recarga la página). */
  cambiar: (id: number) => void;
  recargar: () => Promise<void>;
}

const AgenteCtx = createContext<Ctx>({
  agentes: [], actual: null, limite: 1, usados: 0, enPrueba: false, cargando: true,
  cambiar: () => {}, recargar: async () => {},
});

export const AgenteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [agentes, setAgentes] = useState<AgenteResumen[]>([]);
  const [limite, setLimite] = useState(1);
  const [usados, setUsados] = useState(0);
  const [enPrueba, setEnPrueba] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [actualId, setActualId] = useState<number | null>(() => {
    const v = parseInt(localStorage.getItem(CLAVE_AGENTE) || '', 10);
    return Number.isFinite(v) ? v : null;
  });

  const recargar = useCallback(async () => {
    try {
      const { data } = await api.get('/agente/lista');
      const lista: AgenteResumen[] = data.agentes || [];
      setAgentes(lista);
      setLimite(data.limite || 1);
      setUsados(data.usados || lista.length);
      setEnPrueba(!!data.enPrueba);
      // Si el agente guardado ya no existe, volver al principal.
      const guardado = parseInt(localStorage.getItem(CLAVE_AGENTE) || '', 10);
      if (!lista.some((a) => a.id === guardado)) {
        const principal = lista.find((a) => a.es_principal) || lista[0];
        if (principal) {
          localStorage.setItem(CLAVE_AGENTE, String(principal.id));
          setActualId(principal.id);
        } else {
          localStorage.removeItem(CLAVE_AGENTE);
        }
      }
    } catch { /* sin sesión o error de red: el panel sigue con el agente principal */ }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { recargar(); }, [recargar]);

  const cambiar = (id: number) => {
    if (id === actualId) return;
    localStorage.setItem(CLAVE_AGENTE, String(id));
    window.location.reload();
  };

  const actual = agentes.find((a) => a.id === actualId) || agentes.find((a) => a.es_principal) || null;

  return (
    <AgenteCtx.Provider value={{ agentes, actual, limite, usados, enPrueba, cargando, cambiar, recargar }}>
      {children}
    </AgenteCtx.Provider>
  );
};

export const useAgentes = () => useContext(AgenteCtx);
